import { AnimalType } from './animal';
import { FarmType } from './farm';
import { FarmContactViewModel } from './user';

export interface FarmSettingsViewModel {
  farmId: string;
  dashboardBarChartSettings: BarChartData[];
  contacts: FarmContactViewModel[];
  farmType: FarmType;
  options: FarmSettingsOptions;
}

export interface BarChartData {
  animalType: AnimalType;
  /**
   * Hides this animal type's medication chart from the dashboard. Phrased as "disabled" so the chart is
   * enabled by default: settings stored before the flag existed have no matching key, and a missing key
   * reads as false. The configured supplies survive while the chart is off, so re-enabling restores them.
   */
  isDisabled?: boolean;
  barChartSupplies: BarChartMedicalSupply[];
}

export interface BarChartMedicalSupply {
  medicalSupply: string;
  medicalSupplyId: string;
}

export interface FarmSettingsOptions {
  weeksCycle: number;
  taskListWorkerEnabled: boolean;
  createTaskListDay: number;
  earlyWarningWorkerEnabled: boolean;
  animalsWorkerEnabled: boolean;
  unweanedPigletHoldingMoveEnabled: boolean;
  daysUntilHoldingMove: number;
  holdingRoomLocationId?: string | null;
  timeZoneId?: string;
  /**
   * Hides the piglet registration cards and weekly chart from the dashboard. Phrased as "disabled" for the
   * same reason as {@link BarChartData.isDisabled}: absent means enabled.
   */
  pigletRegistrationWidgetDisabled?: boolean;
}

export interface AnimalCapacityViewModel {
  sow: number;
  gilt: number;
  piglet: number;
  fattening: number;
  cow: number;
  goat: number;
  calf: number;
}

export interface WebNotificationViewModel {
  id: string;
  message: string;
  dateTime: Date;
  isViewed: boolean;
  info: string;
}

export interface NotificationsQueryResult {
  notifications: WebNotificationViewModel[];
}

export interface TranslationViewModel {
  id: string;
  isoCode: string;
  language: string;
  updated: Date | null;
}

//#region app event log model(s)
export interface AppEventLog {
  id: string;
  dateTime: Date;
  userInfo: AppEventLogUserInfo;
  application: AppEventLogApplication;
  payload: AppEventLogPayload;
}

export interface AppEventLogPayload {
  isError: boolean;
  action: string;
  message: string;
  stackTrace: string;
  additionalInfo: string;
}

export interface AppEventLogApplication {
  type: AppEventLogApplicationType;
  version: string;
  languageIso: string;
}

export interface AppEventLogUserInfo {
  requestIpAddress: string;
  user: AppEventLogUser;
  injector: AppEventLogInjectorInfo;
  mobile: AppEventLogMobileInfo;
}

export interface AppEventLogUser {
  farmId: string;
  userId: string;
  veterinaryId: string;
  name: string;
  role: string;
}

export interface AppEventLogMobileInfo {
  hardwareModel: string;
  operatingSystemVersion: string;
  type: MobileInfoType;
}

export interface AppEventLogInjectorInfo {
  application: string;
  bootloader: string;
  bluetooth: string;
  macAddress: string;
  name: string;
}

export enum AppEventLogApplicationType {
  System = 10,
  Web = 20,
  Mobile = 30,
  Injector = 40,
  Reader = 50,
  SlaughterHouse = 60
}

export enum MobileInfoType {
  Android = 100,
  iOs = 200
}
//#endregion
